/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState } from "react";

import { ArrowLeftIcon, ArrowRightIcon } from "@/assets/icons";
import { ChartTooltip } from "@/components/ui/chart/ChartTooltip";
import ToolTipBase from "@/components/ui/tooltip";
import { compactNumber } from "@/utils/number";

import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  ResponsiveContainerProps,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { twMerge } from "tailwind-merge";

import { domainTickCalculate } from "./utils";

const MAX_VISIBLE_BARS = 10;

const XAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const maxLength = 14;
  const fullLabel = payload.value;

  const displayLabel =
    fullLabel.length > maxLength
      ? `${fullLabel.slice(0, maxLength)}...`
      : fullLabel;

  return (
    <g transform={`translate(${x},${y})`}>
      {fullLabel.length > maxLength ? (
        <ToolTipBase content={fullLabel} position="top-center">
          <text
            x={-15}
            y={10}
            dy={10}
            textAnchor="middle"
            fill="#000000B2"
            transform="rotate(-45)"
            fontSize={12}
            style={{
              cursor: "pointer"
            }}
          >
            {displayLabel}
          </text>
        </ToolTipBase>
      ) : (
        <text
          x={-15}
          y={10}
          dy={10}
          textAnchor="middle"
          fill="#000000B2"
          transform="rotate(-45)"
          fontSize={12}
          style={{
            cursor: "default"
          }}
        >
          {displayLabel}
        </text>
      )}
    </g>
  );
};

interface VerticalBarChartProps
  extends Omit<ResponsiveContainerProps, "children"> {
  data: {
    name: string;
    key: number;
  }[];
  title?: string;
  onBarClick?: (name: string) => void;
  isResponsive?: boolean;
}

interface VerticalBarChartItemProps {
  width?: number;
  height?: number;
  chartData: {
    name: string;
    key: string;
    value: number;
  }[];
  axisDomainMax: number;
  axisTicks: number[];
  totalValue: number;
  onBarClick?: (name: string) => void;
  title?: string;
}

const VerticalBarChartItem = ({
  width,
  height,
  chartData,
  axisDomainMax,
  axisTicks,
  totalValue,
  onBarClick,
  title
}: VerticalBarChartItemProps) => {
  return (
    <BarChart
      width={width}
      height={height}
      data={chartData}
      margin={{ top: 20, right: 20, left: 0, bottom: -15 }}
      aria-label={title}
    >
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis
        dataKey="name"
        type="category"
        height={80}
        tickLine={false}
        interval={0}
        tick={<XAxisTick />}
      />
      <YAxis
        dataKey="value"
        type="number"
        domain={[0, axisDomainMax * 1.2]}
        ticks={axisTicks}
        allowDecimals={false}
        tickLine={false}
        axisLine={false}
        tickFormatter={(v) => compactNumber(v)}
        tick={{ fill: "#666", fontSize: 12, fontWeight: 500 }}
      />
      <Tooltip
        content={(content) => (
          <ChartTooltip
            {...content}
            totalValue={totalValue}
            getLabelFromItem={(item) => item.payload.key}
            formatPercentage={(percent) => percent.toFixed(1)}
          />
        )}
      />
      <Bar
        dataKey="value"
        fill="#088C7A"
        className="font-semibold text-[#000000]/70"
        maxBarSize={40}
        radius={[999, 999, 0, 0]}
        onClick={(data) => onBarClick?.(data.name)}
      />
    </BarChart>
  );
};

const VerticalBarChart = React.forwardRef<
  HTMLDivElement,
  VerticalBarChartProps
>((props, ref) => {
  const [currentPage, setCurrentPage] = useState(0);
  const { data, title, onBarClick, isResponsive = true, ...rest } = props;

  // Transform data to have a consistent "value" key for Recharts
  // Extract the first non-"name" property dynamically
  const chartData = data.map((item) => {
    const dataItem = item as Record<string, string | number>;
    const key = Object.keys(dataItem)[1];
    const valueKey = Object.keys(dataItem).find((k) => k !== "name");
    const value = valueKey ? Number(dataItem[valueKey]) : Number(item.key);

    return {
      name: item.name,
      key: key,
      value: value || 0
    };
  });

  const totalPages = Math.ceil(chartData.length / MAX_VISIBLE_BARS);
  const startIndex = currentPage * MAX_VISIBLE_BARS;
  const endIndex = startIndex + MAX_VISIBLE_BARS;
  const visibleData = chartData.slice(startIndex, endIndex);
  const showNavigation = chartData.length > MAX_VISIBLE_BARS;

  // Reset to first page when data changes
  React.useEffect(() => {
    if (isResponsive) {
      setCurrentPage(0);
    }
  }, [data.length, isResponsive]);

  const BAR_WIDTH = 40; // default bar max width
  const MIN_CHART_WIDTH = 424; // default minimum chart width to prevent excessive shrinking when there are few bars
  const chartWidth = Math.max(chartData.length * BAR_WIDTH, MIN_CHART_WIDTH);

  const numericValues = chartData
    .map((item) => item.value)
    .filter((v) => !Number.isNaN(v));
  const maxValue = numericValues.length > 0 ? Math.max(...numericValues, 0) : 0;

  const { axisDomainMax, axisTicks } = React.useMemo(
    () => domainTickCalculate(numericValues, maxValue),
    [numericValues, maxValue]
  );

  const totalValue = numericValues.reduce((sum, val) => sum + val, 0);

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  if (!data?.length || totalValue === 0)
    return (
      <div className="flex-center h-[400px] px-4">
        <p className="text-base text-txtHeading font-semibold text-center">
          No data available to display. Please check your data or try adjusting
          the configuration menu.
        </p>
      </div>
    );

  return (
    <div
      ref={ref}
      className="w-full relative flex items-center overflow-x-auto"
    >
      {isResponsive ? (
        <>
          {showNavigation && (
            <button
              onClick={handlePrevious}
              disabled={currentPage === 0}
              aria-label="Previous page"
              className={twMerge(
                "flex items-center justify-center w-8 h-8 rounded-full transition-colors flex-shrink-0 mr-2",
                currentPage === 0
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
              )}
            >
              <ArrowLeftIcon className="w-8 h-8" />
            </button>
          )}
          <ResponsiveContainer width="100%" height={rest.height || 400}>
            <VerticalBarChartItem
              chartData={visibleData}
              axisDomainMax={axisDomainMax}
              axisTicks={axisTicks}
              totalValue={totalValue}
              onBarClick={onBarClick}
              title={title}
            />
          </ResponsiveContainer>
          {showNavigation && (
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages - 1}
              aria-label="Next page"
              className={twMerge(
                "flex items-center justify-center w-8 h-8 rounded-full transition-colors flex-shrink-0 ml-2 fill-dim-gray",
                currentPage === totalPages - 1
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
              )}
            >
              <ArrowRightIcon className="w-8 h-8" />
            </button>
          )}
        </>
      ) : (
        <div style={{ width: chartWidth }}>
          <VerticalBarChartItem
            width={chartWidth}
            height={typeof rest.height === "number" ? rest.height : 400}
            chartData={chartData}
            axisDomainMax={axisDomainMax}
            axisTicks={axisTicks}
            totalValue={totalValue}
            onBarClick={onBarClick}
            title={title}
          />
        </div>
      )}
    </div>
  );
});

VerticalBarChart.displayName = "VerticalBarChart";

export default VerticalBarChart;
